// Schema.org Structured Data Templates for Wormble

export const getWebApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Wormble",
  "description": "Daily word guessing puzzle game. Guess the hidden 5-letter word in 5 tries.",
  "url": "https://wormble.com",
  "applicationCategory": "GameApplication",
  "operatingSystem": "Any",
  "browserRequirements": "Requires JavaScript. Requires HTML5.",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "author": {
    "@type": "Organization",
    "name": "Wormble",
    "url": "https://wormble.com"
  }
});

export const getPuzzleArticleSchema = (puzzleNumber: number, date: string) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": `Wormble Puzzle #${puzzleNumber} - ${date}`,
  "description": `Daily word puzzle for ${date}. Guess the 5-letter word in 5 tries.`,
  "datePublished": new Date(date).toISOString(),
  "dateModified": new Date(date).toISOString(),
  "author": {
    "@type": "Organization",
    "name": "Wormble",
    "url": "https://wormble.com"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Wormble",
    "url": "https://wormble.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://wormble.com/og-image.png"
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://wormble.com/puzzle/${date}`
  },
  "image": "https://wormble.com/og-image.png"
});

export const getBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const getGamePlayActionSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Game",
  "name": "Wormble",
  "description": "Daily word guessing puzzle game",
  "gameItem": {
    "@type": "Thing",
    "name": "5-Letter Word Puzzle"
  },
  "numberOfPlayers": {
    "@type": "QuantitativeValue",
    "minValue": 1,
    "maxValue": 1
  },
  "playMode": "SinglePlayer"
});

export const getFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Wormble",
  "url": "https://wormble.com",
  "logo": "https://wormble.com/og-image.png",
  "description": "Daily word puzzle game with unique 5-try mechanics",
  "foundingDate": "2024",
  "sameAs": []
});

export const getWebPageSchema = (name: string, description: string, url: string) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": name,
  "description": description,
  "url": url,
  "isPartOf": {
    "@type": "WebSite",
    "name": "Wormble",
    "url": "https://wormble.com"
  }
});

export const getHowToPlaySchema = () => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Play Wormble",
  "description": "Learn how to play Wormble, the daily word puzzle game",
  "step": [
    {
      "@type": "HowToStep",
      "position": 1,
      "name": "Enter a word",
      "text": "Type a valid 5-letter word using the keyboard"
    },
    {
      "@type": "HowToStep",
      "position": 2,
      "name": "Check feedback",
      "text": "Green = correct position, Yellow = wrong position, Gray = not in word"
    },
    {
      "@type": "HowToStep",
      "position": 3,
      "name": "Refine guesses",
      "text": "Use the color feedback to narrow down the solution"
    },
    {
      "@type": "HowToStep",
      "position": 4,
      "name": "Solve in 5 tries",
      "text": "Find the correct word within 5 attempts to win"
    }
  ],
  "totalTime": "PT5M"
});

export const getArticleSchema = (
  headline: string,
  description: string,
  url: string,
  datePublished: string,
  dateModified: string
) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": headline,
  "description": description,
  "url": url,
  "datePublished": datePublished,
  "dateModified": dateModified,
  "author": {
    "@type": "Organization",
    "name": "Wormble",
    "url": "https://wormble.com"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Wormble",
    "url": "https://wormble.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://wormble.com/og-image.png"
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": url
  },
  "image": "https://wormble.com/og-image.png"
});

export const getBlogPostingSchema = (
  headline: string,
  description: string,
  url: string,
  datePublished: string,
  dateModified: string,
  wordCount: number
) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": headline,
  "description": description,
  "url": url,
  "datePublished": datePublished,
  "dateModified": dateModified,
  "wordCount": wordCount,
  "author": {
    "@type": "Organization",
    "name": "Wormble",
    "url": "https://wormble.com"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Wormble",
    "url": "https://wormble.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://wormble.com/og-image.png"
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": url
  },
  "image": "https://wormble.com/og-image.png"
});

export const getBlogSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "Wormble Blog",
  "description": "Strategy guides, tips, and insights for Wormble players",
  "url": "https://wormble.com/blog",
  "publisher": {
    "@type": "Organization",
    "name": "Wormble",
    "url": "https://wormble.com"
  }
});
